import * as React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DollarFormatter, PercentageFormatter } from '@src/components';

import {
  safeNumber,
  getStatusPillStyles,
  getFormatTone,
  getVerdictTone,
  getRecommendationTone,
  getComConfidenceTone,
  getWhyDriverTone,
  formatWhyDriverChip,
} from './utils';

const renderStatusPill = (label, tone = 'neutral') => {
  const colors = getStatusPillStyles(tone);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.1,
        py: 0.45,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        backgroundColor: colors.bg,
        color: colors.fg,
      }}
    >
      {label || '-'}
    </Box>
  );
};

export default function AdPreviewDialog({
  previewRow,
  pauseNote,
  pauseLoading,
  onPauseNoteChange,
  onPause,
  onClose,
}) {
  if (!previewRow) return null;

  return (
    <Dialog open={Boolean(previewRow)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{previewRow.adName || 'Ad Preview'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">Campaign</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {previewRow.campaign || 'No campaign stored.'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">Ad Set</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {previewRow.adSetName || 'No ad set stored.'}
              </Typography>
            </Box>
          </Stack>
          {previewRow.imageUrl || previewRow.thumbnailUrl ? (
            <Box
              component="img"
              src={previewRow.imageUrl || previewRow.thumbnailUrl}
              alt={previewRow.adName || 'Ad creative preview'}
              sx={{
                width: '100%',
                maxHeight: 360,
                objectFit: 'contain',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.100',
              }}
            />
          ) : null}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} flexWrap="wrap">
            {renderStatusPill(previewRow.facebookFormat || 'Unknown', getFormatTone(previewRow.facebookFormat))}
            {renderStatusPill(previewRow.creativeVerdict || 'Unknown', getVerdictTone(previewRow.creativeVerdict))}
            {renderStatusPill(previewRow.overallRecommendation || 'Unknown', getRecommendationTone(previewRow.overallRecommendation))}
            {previewRow.comConfidenceLabel ? renderStatusPill(previewRow.comConfidenceLabel, getComConfidenceTone(previewRow.comConfidenceLabel)) : null}
            {previewRow.dealValueStability ? renderStatusPill(previewRow.dealValueStability, 'neutral') : null}
            {previewRow.ctaType ? renderStatusPill(previewRow.ctaType, 'neutral') : null}
          </Stack>
          <Box>
            <Typography variant="overline" color="text.secondary">Headline</Typography>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              {previewRow.headline || 'No headline stored.'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">Primary Text</Typography>
            <Typography variant="body2">
              {previewRow.primaryText || 'No primary text stored.'}
            </Typography>
          </Box>
          {previewRow.description ? (
            <Box>
              <Typography variant="overline" color="text.secondary">Description</Typography>
              <Typography variant="body2">{previewRow.description}</Typography>
            </Box>
          ) : null}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">Why</Typography>
              <Typography variant="body2">{previewRow.whyLabel || '-'}</Typography>
              {Array.isArray(previewRow.whyDriverChips) && previewRow.whyDriverChips.length ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                  {previewRow.whyDriverChips.map((driver) => (
                    <React.Fragment key={`preview-${driver.key}-${driver.label}`}>
                      {renderStatusPill(formatWhyDriverChip(driver), getWhyDriverTone(driver.directionalDelta))}
                    </React.Fragment>
                  ))}
                </Box>
              ) : null}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">Destination</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {previewRow.destinationUrl || 'No destination URL stored.'}
              </Typography>
            </Box>
          </Stack>
          {previewRow.videoId ? (
            <Typography variant="body2" color="text.secondary">
              Stored video id: {previewRow.videoId}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <TextField
          label="Final Pause Note"
          value={pauseNote}
          onChange={(event) => onPauseNoteChange(event.target.value)}
          size="small"
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
          disabled={!previewRow.isActive || pauseLoading}
        />
        <Button onClick={onClose} disabled={pauseLoading}>Close</Button>
        <Button
          variant="contained"
          color="error"
          onClick={onPause}
          disabled={!previewRow.isActive || pauseLoading || !previewRow.adId || !previewRow.clientId}
        >
          {pauseLoading ? 'Pausing...' : 'Pause Ad'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
